import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, RefreshCw, Edit, X } from "lucide-react";

export const AdminSubscriptionsTab = ({
  allSubscriptions,
  syncingStripeData,
  handleSyncStripeData,
  setSelectedSubMerchant,
  setSelectedAddonsForAdmin,
  setShowManageAddonsModal,
  handleCancelSubscription,
}) => {
  return (
    <div className="space-y-6" data-testid="admin-subscriptions-tab">
      <div className="flex items-center justify-between">
        <span className="text-slate-500">{allSubscriptions.length} merchant subscriptions</span>
        <Button
          onClick={handleSyncStripeData}
          disabled={syncingStripeData}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="sync-stripe-btn"
        >
          {syncingStripeData ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Syncing...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Sync from Stripe
            </>
          )}
        </Button>
      </div>

      {allSubscriptions.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <RefreshCw className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="font-heading font-semibold text-slate-900 mb-2">No Subscriptions Yet</h3>
          <p className="text-slate-500 mb-4">Merchant subscriptions will appear here.</p>
          <Button onClick={handleSyncStripeData} disabled={syncingStripeData} variant="outline">
            {syncingStripeData ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Sync from Stripe
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Merchant</TableHead>
                <TableHead>Plan / Add-ons</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Next Billing</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allSubscriptions.map((sub) => (
                <TableRow key={sub.user_id} data-testid={`sub-row-${sub.user_id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium text-slate-900">{sub.merchant_name || "N/A"}</p>
                      <p className="text-sm text-slate-500">{sub.merchant_email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                        5% Commission
                      </span>
                      {sub.version === "v2" && sub.addons && sub.addons.length > 0 ? (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {sub.crm && (
                            <span className="px-2 py-0.5 text-xs rounded bg-indigo-100 text-indigo-700">CRM</span>
                          )}
                          {sub.pos && (
                            <span className="px-2 py-0.5 text-xs rounded bg-violet-100 text-violet-700">POS</span>
                          )}
                          {sub.services && (
                            <span className="px-2 py-0.5 text-xs rounded bg-fuchsia-100 text-fuchsia-700">Services</span>
                          )}
                        </div>
                      ) : sub.plan_name ? (
                        <span className="block text-xs text-slate-500 mt-1">Legacy: {sub.plan_name}</span>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        sub.status === "active"
                          ? "bg-emerald-100 text-emerald-700"
                          : sub.status === "cancelled"
                          ? "bg-red-100 text-red-700"
                          : sub.status === "past_due"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {sub.status}
                      {sub.cancel_at_period_end && " (cancelling)"}
                    </span>
                  </TableCell>
                  <TableCell className="font-medium">5% commission</TableCell>
                  <TableCell>
                    {sub.current_period_end
                      ? new Date(sub.current_period_end).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                        })
                      : "N/A"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedSubMerchant(sub);
                          setSelectedAddonsForAdmin(sub.addons || []);
                          setShowManageAddonsModal(true);
                        }}
                        className="rounded-full"
                        data-testid={`manage-addons-btn-${sub.user_id}`}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Manage Add-ons
                      </Button>
                      {sub.status === "active" && !sub.cancel_at_period_end && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCancelSubscription(sub.user_id, false)}
                          className="border-red-200 text-red-600 hover:bg-red-50 rounded-full"
                          data-testid={`cancel-sub-btn-${sub.user_id}`}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
